/**
 * 「历史快照」区 —— **`AC-N4.21` 端侧半边的主体**（`N4 §5.4.2` · `DR-039` **D6**）
 *
 * 依据：N 域 PRD **§7.4 `AC-N4.21`**（「覆盖前快照**用户可见、可回滚**」· 必测）·
 * **`DR-039` D6**（「设置页「数据主权」区新增「历史快照」列表；逐行显示 `时间 / 来源 / 体积`，
 * 两个动作：**下载** · **回滚到此**」）。
 *
 * ## 🔴 本组件是"入口 + 展示"，不是"口径"
 *
 * | 动作 | 谁实现 | 本组件做什么 |
 * |---|---|---|
 * | 列出快照 | `SnapshotService.list()`（批次 `67`） | **只展示**下发的字段 |
 * | 下载 | `SnapshotService.download()` | 走 `downloadAuthorized`（与两个导出同一实现） |
 * | 回滚 | `SnapshotService.rollback()`（= 一次 `overwrite`） | **只转发** `{ snapshotId }` |
 * | 保留几份 | 服务端（写入事务内） | **不判断** —— 只显示下发的 `keepLimit` |
 *
 * ## 🔴 「来源」的中文、体积文本一律**不在这里写**
 *
 * 全部经 `features/snapshot.ts` 转发到**共享层**（`snapshotReasonLabel` / `formatBytes`）——
 * 端侧自己写一份映射 = 服务端加第三种原因时界面**静默显示英文 key**（铁律 24）。
 *
 * ## 🔴 为什么用 `useQuery` 的**三态分开守**
 *
 * `isPending` 不排除"已失败"（失败态 `data` 也是 `undefined`）⇒ 只写 `isPending` 会让
 * `data` 报 `possibly 'undefined'`（项目已登记的坑）。⇒ `isPending` / `isError` **两条分开守**。
 *
 * ## ⚠️ 不可静态断言的部分（诚实登记）
 *
 * - **点「下载」之后**：`fetch → blob → <a download>` 三步需要真浏览器（铁律 43）；
 * - **回滚的确会改库**：那要真服务端（冒烟脚本 + 人工走查）。
 *
 * ⇒ 本组件的 spec 只断言**渲染输入**（行的三段文本 / 两个按钮 / owner 禁用 / 空态）。
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { localDayOf, type RestoreResultView, type SnapshotListView } from '@jiaying/shared';
import { downloadAuthorized } from '../api/download.js';
import { listSnapshots, rollbackSnapshot, snapshotDownloadPath } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useApp } from '../app/store.js';
import { integrityText } from '../features/restore.js';
import {
  formatSizeText,
  orderedSnapshots,
  buildRollbackRequest,
  encodeSnapshotId,
  snapshotRowText,
} from '../features/snapshot.js';
import {
  SNAPSHOT_DOWNLOAD_ACTION,
  SNAPSHOT_DOWNLOAD_BUSY,
  SNAPSHOT_EMPTY_NOTE,
  SNAPSHOT_OWNER_ONLY_NOTE,
  SNAPSHOT_ROLLBACK_ACTION,
  SNAPSHOT_SECTION_NOTE,
  SNAPSHOT_SECTION_TITLE,
  snapshotSectionNote,
} from '../features/snapshot-labels.js';
import { RollbackConfirmDialog } from './RollbackConfirmDialog.js';

/** 端侧浏览器时区（只用于**兜底文件名**；首选是解析服务端 `Content-Disposition`） */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * 「历史快照」区块。
 *
 * 🔴 **owner 闸**：三条快照路由都挂 `@RequireOwner(PermissionAction.BulkOverwriteImport)`
 * ⇒ 非 owner 时**禁用而不是隐藏**（隐藏会让人以为"没这个功能"，禁用才说清"轮不到你"——
 * 与同卡「从备份恢复」那处的处置一致）。
 *
 * ⚠️ 本判据**只是 UI 可见性**，不是权限：真正的闸在服务端，绕过 UI 也拿不到数据。
 * 它的作用是**别让用户点一个注定 403 的按钮**。
 */
export function SnapshotSection({
  isOwner,
  timezone = browserTimeZone(),
  onRolledBack,
}: {
  /** 是否 owner（由父级用**服务端成员名单**算好传入 —— 本件不自己推断） */
  isOwner: boolean;
  /** 账本时区（父级可注入；不给则用浏览器时区）—— 只用于把 UTC 毫秒显示成日历日 */
  timezone?: string;
  /** 回滚成功后的回调（父级可据此刷新净资产等其他视图） */
  onRolledBack?: (result: RestoreResultView) => void;
}) {
  const { session } = useApp();
  /** 正在下载的快照 id（`null` = 没有在下载） */
  const [downloading, setDownloading] = useState<string | null>(null);
  /** 待确认回滚的快照 id（`null` = 对话框关着） */
  const [pendingRollback, setPendingRollback] = useState<string | null>(null);
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);
  /** 回滚成功后展示的结果（逐账户余额 + 完整性三项 —— 复用 `RestoreResultView` 的口径） */
  const [rolledBack, setRolledBack] = useState<RestoreResultView | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // 🔴 owner 之外**不发请求**（注定 403，发了只是浪费）—— 与同卡恢复入口同一处置
  const query = useQuery<SnapshotListView>({
    queryKey: ['snapshots'],
    queryFn: listSnapshots,
    enabled: isOwner,
  });

  const runDownload = async (id: string): Promise<void> => {
    setDownloading(id);
    setDownloadError(null);
    try {
      // ⚠️ 兜底名用**快照自身**的日期（与"今天"无关 —— 否则两份不同快照会同名）
      const row = query.data?.items.find((s) => s.id === id);
      const fallback = `jiaying-backup-${session?.ledgerId ?? 'ledger'}-${
        row === undefined ? 'snapshot' : localDayOf(row.createdAt, timezone)
      }.json`;
      await downloadAuthorized(snapshotDownloadPath(encodeSnapshotId(id)), fallback);
    } catch (e: unknown) {
      // 🔴 原样展示服务端 `message`（不自己编文案 —— 见 `http.ts` 文件头）
      setDownloadError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDownloading(null);
    }
  };

  const runRollback = async (): Promise<void> => {
    if (pendingRollback === null) return;
    setRollbackBusy(true);
    setRollbackError(null);
    try {
      const out = await rollbackSnapshot(buildRollbackRequest(pendingRollback));
      setRolledBack(out);
      setPendingRollback(null);
      onRolledBack?.(out);
      // 🔴 回滚会新增一份 `beforeRollback` 快照（`DR-039` §4.3）⇒ 列表必须重取，
      //    否则新还原点要等下次进设置页才出现（用户会以为"回滚没留安全网"）。
      await query.refetch();
    } catch (e: unknown) {
      setRollbackError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRollbackBusy(false);
    }
  };

  return (
    <Box data-testid="snapshot-section">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {SNAPSHOT_SECTION_TITLE}
        </Typography>
        {isOwner && query.data !== undefined ? (
          <Chip
            size="small"
            variant="outlined"
            label={`${String(query.data.items.length)} 份`}
            data-testid="snapshot-count"
          />
        ) : null}
      </Stack>

      <Typography variant="caption" color="text.secondary" component="div" data-testid="snapshot-note">
        {SNAPSHOT_SECTION_NOTE}
      </Typography>

      {!isOwner ? (
        <Typography variant="caption" color="text.secondary" component="div" data-testid="snapshot-owner-note">
          {SNAPSHOT_OWNER_ONLY_NOTE}
        </Typography>
      ) : null}

      {/* ── 三态：pending / error / 空 / 有数据（🔴 分开守，不用一个三元糊住）── */}
      {isOwner && query.isPending ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }} data-testid="snapshot-loading">
          正在读取快照…
        </Typography>
      ) : null}

      {isOwner && query.isError ? (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="snapshot-load-error">
          {query.error instanceof ApiError ? query.error.message : String(query.error)}
        </Alert>
      ) : null}

      {isOwner && !query.isPending && !query.isError && query.data !== undefined ? (
        <>
          <Typography variant="caption" color="text.secondary" component="div" data-testid="snapshot-keep-note">
            {snapshotSectionNote(query.data.keepLimit)}
          </Typography>

          {query.data.items.length === 0 ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }} data-testid="snapshot-empty">
              {SNAPSHOT_EMPTY_NOTE}
            </Typography>
          ) : (
            <List dense disablePadding data-testid="snapshot-list">
              {orderedSnapshots(query.data.items).map((s) => (
                <ListItem
                  key={s.id}
                  disableGutters
                  alignItems="flex-start"
                  data-testid={`snapshot-row-${s.id}`}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={downloading !== null}
                        onClick={() => {
                          void runDownload(s.id);
                        }}
                        data-testid={`snapshot-download-${s.id}`}
                      >
                        {downloading === s.id ? SNAPSHOT_DOWNLOAD_BUSY : SNAPSHOT_DOWNLOAD_ACTION}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => {
                          setRollbackError(null);
                          setPendingRollback(s.id);
                        }}
                        data-testid={`snapshot-rollback-${s.id}`}
                      >
                        {SNAPSHOT_ROLLBACK_ACTION}
                      </Button>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Box component="span" data-testid={`snapshot-row-text-${s.id}`}>
                        {snapshotRowText(s, localDayOf(s.createdAt, timezone))}
                      </Box>
                    }
                    secondary={
                      // 🔴 体积单独给一个 testid：**成对断言**要用它证"体积确实画了"，
                      //    而不是靠整行 contains（整行里其它两段也在，断言会是空转）
                      <Box component="span" data-testid={`snapshot-size-${s.id}`}>
                        {formatSizeText(s.sizeBytes)}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {downloadError !== null ? (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="snapshot-download-error">
              {downloadError}
            </Alert>
          ) : null}

          {rolledBack !== null ? (
            <Alert severity="success" sx={{ mt: 1 }} data-testid="snapshot-rollback-done">
              {/* 🔴 完整性三项复用 `features/restore.ts` 的 `integrityText`（与恢复结果区**同一份文案实现**）——
                  不在这里再拼一遍（那会让"回滚报告"与"恢复报告"两处措辞漂移，铁律 24） */}
              已回滚。{integrityText(rolledBack.integrity).text}
            </Alert>
          ) : null}
        </>
      ) : null}

      <Divider sx={{ my: 1.5 }} />

      {/*
        🔴 二次确认对话框 —— 内容层单独导出（Portal ⇒ 静态渲染拿不到，见文件头）
        ⚠️ 它**不在** `isOwner` 分支内：关着的时候也不该影响渲染产物；
           但放在这里（而非条件里）让"外壳恒在产物中、内容不在"这条断言稳定。
      */}
      <RollbackConfirmDialog
        open={pendingRollback !== null}
        busy={rollbackBusy}
        error={rollbackError}
        onConfirm={() => {
          void runRollback();
        }}
        onClose={() => {
          if (rollbackBusy) return;
          setPendingRollback(null);
          setRollbackError(null);
        }}
      />
    </Box>
  );
}
